import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { usePracticeAccess, useAuth } from '@hb/react'
import {
  DeliveryType,
  ENCOUNTERS,
  EncounterType,
  encounterTypeName,
  MIDWIVES,
  PDF_EXPORT_LOG,
} from '@hb/shared'
import {
  addDoc,
  collection,
  DocumentData,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import { useCallback, useState } from 'react'
import { db } from '@backend/db/db'
import { useIsPracticeSuperAdmin } from '@hooks/backend/auth/usePracticeRole'

type QueryType = 'icd10' | 'procedureType' | 'deliveryOutcome'

const QUERY_TYPE_LABELS: Record<QueryType, string> = {
  icd10: 'By ICD-10 Diagnosis Code',
  procedureType: 'By Encounter / Procedure Type',
  deliveryOutcome: 'By Delivery Outcome',
}

const DELIVERY_TYPE_LABELS: Record<DeliveryType, string> = {
  nsvd: 'NSVD (Normal Spontaneous Vaginal Delivery)',
  cSection: 'Cesarean Section',
  breech: 'Breech',
  vacuum: 'Vacuum-Assisted',
  forceps: 'Forceps-Assisted',
}

const PROCEDURE_ENCOUNTER_TYPES: { value: EncounterType; label: string }[] = [
  { value: EncounterType.ANTEPARTUM, label: encounterTypeName[EncounterType.ANTEPARTUM] },
  { value: EncounterType.POSTPARTUM, label: encounterTypeName[EncounterType.POSTPARTUM] },
  {
    value: EncounterType.BIRTHING_CENTER_ADMISSION,
    label: encounterTypeName[EncounterType.BIRTHING_CENTER_ADMISSION],
  },
  { value: EncounterType.ULTRASOUND, label: encounterTypeName[EncounterType.ULTRASOUND] },
  { value: EncounterType.WELL_WOMAN, label: encounterTypeName[EncounterType.WELL_WOMAN] },
  { value: EncounterType.PHYSICAL, label: encounterTypeName[EncounterType.PHYSICAL] },
]

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '—'
  return dateStr
}

function formatDeliveryDate(ms: number | null | undefined) {
  if (!ms) return '—'
  return new Date(ms).toLocaleDateString()
}

function anonymize(id: string | null | undefined) {
  if (!id) return '—'
  return id.slice(0, 6) + '…'
}

function escapeCsvCell(value: string | number | null | undefined) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`
}

export const QAReportsView = () => {
  const role = useAuth(s => s.role)
  const { selectedPracticeId } = usePracticeAccess()
  const isPracticeSuperAdmin = useIsPracticeSuperAdmin(selectedPracticeId ?? '')
  const authUser = useAuth(s => s.authUser)

  const canAccess = role === 'super-admin' || isPracticeSuperAdmin

  const [queryType, setQueryType] = useState<QueryType>('icd10')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [icd10Input, setIcd10Input] = useState('')
  const [selectedEncounterType, setSelectedEncounterType] = useState<EncounterType>(
    EncounterType.ANTEPARTUM,
  )
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryType | 'all'>('all')
  const [results, setResults] = useState<DocumentData[]>([])
  const [loading, setLoading] = useState(false)
  const [hasQueried, setHasQueried] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const practiceId = selectedPracticeId ?? ''

  const runQuery = useCallback(async () => {
    if (!practiceId || !startDate || !endDate) {
      setError('Please select a practice and set start/end dates.')
      return
    }
    setError(null)
    setLoading(true)
    setHasQueried(false)
    try {
      let docs: DocumentData[] = []

      if (queryType === 'icd10') {
        const code = icd10Input.trim().toUpperCase()
        if (!code) {
          setError('Please enter an ICD-10 code.')
          setLoading(false)
          return
        }
        const q = query(
          collection(db, ENCOUNTERS),
          where('practiceId', '==', practiceId),
          where('icd10Codes', 'array-contains', code),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc'),
        )
        const snap = await getDocs(q)
        snap.forEach(d => docs.push({ id: d.id, ...d.data() }))
      } else if (queryType === 'procedureType') {
        const q = query(
          collection(db, ENCOUNTERS),
          where('practiceId', '==', practiceId),
          where('type', '==', selectedEncounterType),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc'),
        )
        const snap = await getDocs(q)
        snap.forEach(d => docs.push({ id: d.id, ...d.data() }))
      } else {
        const startMs = new Date(startDate).getTime()
        const endMs = new Date(endDate + 'T23:59:59').getTime()
        // Query the midwife subcollection — rule allows isPracticeAdmin(midwifeId) reads.
        // Top-level assessment-snippets is HB-admin-only; subcollection is practice-scoped.
        const q = query(
          collection(db, MIDWIVES, practiceId, 'assessment-snippets'),
          where('deliveredOn', '>=', startMs),
          where('deliveredOn', '<=', endMs),
          orderBy('deliveredOn', 'desc'),
        )
        const snap = await getDocs(q)
        snap.forEach(d => docs.push({ id: d.id, ...d.data() }))
        if (selectedDeliveryType !== 'all') {
          docs = docs.filter(d => {
            const types: DeliveryType[] | undefined = d.deliveryTypes
            return types?.includes(selectedDeliveryType)
          })
        }
      }

      setResults(docs)
      setHasQueried(true)
    } catch (e) {
      setError(`Query failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setLoading(false)
    }
  }, [
    practiceId,
    queryType,
    startDate,
    endDate,
    icd10Input,
    selectedEncounterType,
    selectedDeliveryType,
  ])

  const logExportAudit = useCallback(() => {
    if (!authUser) return
    addDoc(collection(db, PDF_EXPORT_LOG), {
      exportedAt: Date.now(),
      exportedBy: authUser.uid,
      queryType,
      practiceId,
      startDate,
      endDate,
      resultCount: results.length,
      format: 'csv',
      type: 'qa-report-csv',
    }).catch(() => {})
  }, [authUser, queryType, practiceId, startDate, endDate, results.length])

  const handleExportCsv = useCallback(() => {
    let headers: string[]
    let rows: string[][]

    if (queryType === 'icd10') {
      headers = ['Date', 'Patient ID', 'Encounter Type', 'ICD-10 Codes', 'Auth Status']
      rows = results.map(r => [
        r.date ?? '',
        anonymize(r.patientId),
        encounterTypeName[r.type as EncounterType] ?? r.type ?? '',
        (r.icd10Codes as string[] | undefined)?.join('; ') ?? '',
        r.status ?? '',
      ])
    } else if (queryType === 'procedureType') {
      headers = ['Date', 'Patient ID', 'Encounter Type', 'Auth Status']
      rows = results.map(r => [
        r.date ?? '',
        anonymize(r.patientId),
        encounterTypeName[r.type as EncounterType] ?? r.type ?? '',
        r.status ?? '',
      ])
    } else {
      headers = ['Delivery Date', 'Patient ID', 'First Name', 'Last Name']
      rows = results.map(r => [
        formatDeliveryDate(r.deliveredOn as number | undefined),
        anonymize(r.patientId as string | undefined),
        r.firstName ?? r.fname ?? '',
        r.lastName ?? r.lname ?? '',
      ])
    }

    const csv = [headers, ...rows].map(row => row.map(escapeCsvCell).join(',')).join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `qa-report-${queryType}-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    logExportAudit()
  }, [queryType, results, logExportAudit])

  if (!canAccess) {
    return (
      <Box p={8} maxW="600px" mx="auto">
        <Alert status="warning" borderRadius={4}>
          <AlertIcon />
          <Text>Access restricted to practice super-admins and HB super-admins.</Text>
        </Alert>
      </Box>
    )
  }

  return (
    <VStack align="stretch" spacing={6} p={6} maxW="1200px" mx="auto">
      <Text fontSize="xl" fontWeight={700}>
        QA Reports
      </Text>
      <Text fontSize="sm" color="gray.600">
        Query encounters and delivery data for QA compliance reporting (10 NYCRR 405.10(a)(5)). All
        CSV exports are logged in the audit trail.
      </Text>

      {/* Query Builder */}
      <Box bg="white" borderRadius={6} p={4} border="1px solid" borderColor="gray.200">
        <VStack align="stretch" spacing={4}>
          <HStack spacing={3} flexWrap="wrap">
            <Box minW="220px">
              <Text fontSize="xs" fontWeight={600} mb={1} color="gray.600">
                Query Type
              </Text>
              <Select
                size="sm"
                value={queryType}
                onChange={e => {
                  setQueryType(e.target.value as QueryType)
                  setResults([])
                  setHasQueried(false)
                  setError(null)
                }}>
                {(Object.keys(QUERY_TYPE_LABELS) as QueryType[]).map(k => (
                  <option key={k} value={k}>
                    {QUERY_TYPE_LABELS[k]}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="xs" fontWeight={600} mb={1} color="gray.600">
                Start Date
              </Text>
              <Input
                size="sm"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="xs" fontWeight={600} mb={1} color="gray.600">
                End Date
              </Text>
              <Input
                size="sm"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </Box>
          </HStack>

          <HStack spacing={3} flexWrap="wrap">
            {queryType === 'icd10' && (
              <Box minW="220px">
                <Text fontSize="xs" fontWeight={600} mb={1} color="gray.600">
                  ICD-10 Code (exact match, e.g. O09)
                </Text>
                <Input
                  size="sm"
                  placeholder="Enter ICD-10 code"
                  value={icd10Input}
                  onChange={e => setIcd10Input(e.target.value.toUpperCase())}
                />
              </Box>
            )}

            {queryType === 'procedureType' && (
              <Box minW="260px">
                <Text fontSize="xs" fontWeight={600} mb={1} color="gray.600">
                  Encounter / Procedure Type
                </Text>
                <Select
                  size="sm"
                  value={selectedEncounterType}
                  onChange={e => setSelectedEncounterType(e.target.value as EncounterType)}>
                  {PROCEDURE_ENCOUNTER_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Box>
            )}

            {queryType === 'deliveryOutcome' && (
              <Box minW="260px">
                <Text fontSize="xs" fontWeight={600} mb={1} color="gray.600">
                  Delivery Type (optional filter)
                </Text>
                <Select
                  size="sm"
                  value={selectedDeliveryType}
                  onChange={e => setSelectedDeliveryType(e.target.value as DeliveryType | 'all')}>
                  <option value="all">All delivery types</option>
                  {(Object.keys(DELIVERY_TYPE_LABELS) as DeliveryType[]).map(k => (
                    <option key={k} value={k}>
                      {DELIVERY_TYPE_LABELS[k]}
                    </option>
                  ))}
                </Select>
              </Box>
            )}

            <Box alignSelf="flex-end">
              <Button
                size="sm"
                colorScheme="green"
                onClick={runQuery}
                isLoading={loading}
                isDisabled={!practiceId}>
                Run Query
              </Button>
            </Box>
          </HStack>

          {queryType === 'deliveryOutcome' && selectedDeliveryType !== 'all' && (
            <Text fontSize="xs" color="orange.600">
              Note: delivery type filter is applied client-side from loaded records. Only records
              with stored delivery type data will be included.
            </Text>
          )}
        </VStack>
      </Box>

      {/* Error */}
      {error && (
        <Alert status="error" borderRadius={4}>
          <AlertIcon />
          <Text>{error}</Text>
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <HStack justify="center" py={6}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.600">
            Running query…
          </Text>
        </HStack>
      )}

      {/* Results */}
      {hasQueried && !loading && (
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <Text fontSize="sm" fontWeight={600}>
              {results.length} result{results.length !== 1 ? 's' : ''}
            </Text>
            {results.length > 0 && (
              <Button size="sm" variant="outline" onClick={handleExportCsv}>
                Export CSV
              </Button>
            )}
          </HStack>

          {results.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No records found for the selected criteria.
            </Text>
          ) : (
            <Box overflowX="auto" borderRadius={4} border="1px solid" borderColor="gray.200">
              <Table size="sm" bg="white">
                <Thead bg="gray.50">
                  <Tr>
                    {queryType === 'icd10' && (
                      <>
                        <Th>Date</Th>
                        <Th>Patient ID</Th>
                        <Th>Encounter Type</Th>
                        <Th>ICD-10 Codes</Th>
                        <Th>Auth Status</Th>
                      </>
                    )}
                    {queryType === 'procedureType' && (
                      <>
                        <Th>Date</Th>
                        <Th>Patient ID</Th>
                        <Th>Encounter Type</Th>
                        <Th>Auth Status</Th>
                      </>
                    )}
                    {queryType === 'deliveryOutcome' && (
                      <>
                        <Th>Delivery Date</Th>
                        <Th>Patient ID</Th>
                        <Th>First Name</Th>
                        <Th>Last Name</Th>
                      </>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {results.map(r => (
                    <Tr key={r.id}>
                      {queryType === 'icd10' && (
                        <>
                          <Td>{formatDate(r.date)}</Td>
                          <Td fontFamily="mono" fontSize="xs">
                            {anonymize(r.patientId)}
                          </Td>
                          <Td>{encounterTypeName[r.type as EncounterType] ?? r.type ?? '—'}</Td>
                          <Td>{(r.icd10Codes as string[] | undefined)?.join(', ') ?? '—'}</Td>
                          <Td>{r.status ?? '—'}</Td>
                        </>
                      )}
                      {queryType === 'procedureType' && (
                        <>
                          <Td>{formatDate(r.date)}</Td>
                          <Td fontFamily="mono" fontSize="xs">
                            {anonymize(r.patientId)}
                          </Td>
                          <Td>{encounterTypeName[r.type as EncounterType] ?? r.type ?? '—'}</Td>
                          <Td>{r.status ?? '—'}</Td>
                        </>
                      )}
                      {queryType === 'deliveryOutcome' && (
                        <>
                          <Td>{formatDeliveryDate(r.deliveredOn as number | undefined)}</Td>
                          <Td fontFamily="mono" fontSize="xs">
                            {anonymize(r.patientId as string | undefined)}
                          </Td>
                          <Td>{r.firstName ?? r.fname ?? '—'}</Td>
                          <Td>{r.lastName ?? r.lname ?? '—'}</Td>
                        </>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  )
}
